import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import axios from 'axios';
import CountUp from 'react-countup';
import '../CSS/Home.css';
import apiUrl from '../config';
import LayoutDashboard from '../layout/LayoutDashboard';

const getDecodedToken = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    return JSON.parse(atob(token.split('.')[1]));
  } catch (error) {
    return null;
  }
};

const getHomeStatsCacheKey = (role, userId) => {
  return `home_stats_${role || 'unknown'}_${userId || 'global'}`;
};

const Home = () => {
  const navigate = useNavigate();
  const { role, isAuthenticated } = useAuth();

  const today = useMemo(() => new Date(), []);
  const options = { day: 'numeric', month: 'long', year: 'numeric' };
  const formattedDate = today.toLocaleDateString('fr-FR', options);

  const decodedToken = getDecodedToken();
  const currentRole = (role || decodedToken?.role || '').toLowerCase();
  const userId = decodedToken?.user_id;

  const isAdmin = currentRole === 'admin';
  const isCDI = currentRole === 'cdi';

  const [stats, setStats] = useState({
    cdiCount: 0,
    declarationCount: 0,
    paieCount: 0,
    myDeclarationCount: 0,
    myDocCount: 0,
    myPaieCount: 0
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const token = localStorage.getItem('token');

    if (!token) {
      navigate('/login');
      return;
    }

    const decoded = getDecodedToken();
    const effectiveRole = (role || decoded?.role || '').toLowerCase();
    const effectiveUserId = decoded?.user_id;
    const cacheKey = getHomeStatsCacheKey(effectiveRole, effectiveUserId);

    const cachedStats = sessionStorage.getItem(cacheKey);

    if (cachedStats) {
      try {
        setStats(JSON.parse(cachedStats));
        setLoading(false);
      } catch (error) {
        sessionStorage.removeItem(cacheKey);
      }
    } else {
      setLoading(true);
    }

    if (!isAuthenticated && !token) {
      navigate('/login');
      return;
    }

    const headers = { Authorization: `Bearer ${token}` };

    let endpoint = null;

    if (effectiveRole === 'admin') {
      endpoint = `${apiUrl}/stats-summary`;
    }

    if (effectiveRole === 'cdi') {
      endpoint = `${apiUrl}/stats-cdi`;
    }

    if (!endpoint) {
      setLoading(false);
      return;
    }

    axios
      .get(endpoint, { headers })
      .then((response) => {
        if (!isMounted) return;

        let freshStats;

        if (effectiveRole === 'admin') {
          freshStats = {
            cdiCount: response.data.cdi_count || 0,
            declarationCount: response.data.declaration_count || 0,
            paieCount: response.data.paie_count || 0,
            myDeclarationCount: 0,
            myDocCount: 0,
            myPaieCount: 0
          };
        } else {
          freshStats = {
            cdiCount: 0,
            declarationCount: 0,
            paieCount: 0,
            myDeclarationCount: response.data.mes_declarations || 0,
            myDocCount: response.data.mes_documents || 0,
            myPaieCount: response.data.mes_paies || 0
          };
        }

        setStats(freshStats);
        sessionStorage.setItem(cacheKey, JSON.stringify(freshStats));
      })
      .catch((error) => {
        console.error('Erreur chargement accueil:', error);
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, navigate, role]);

  const handleDeclarationClick = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (isAdmin) {
      navigate('/ListeDeclarations');
    } else {
      navigate('/Déclarations');
    }
  };

  return (
    <LayoutDashboard>
      <div className="home-dashboard-page">

        <div className="home-date-pill">
          <span>Nous sommes le</span>
          <strong>{formattedDate}</strong>
        </div>

        <section className="home-welcome-section">
          <div>
            <span className="home-hero-badge">Portail interne</span>
            <h1>Bienvenue dans votre espace Biam Consulting</h1>
            <p>
              Accédez rapidement à vos déclarations, documents administratifs,
              fiches de paie et informations essentielles depuis une interface claire et sécurisée.
            </p>
          </div>
        </section>

        {isAdmin && (
          <section className="home-summary-grid">
            <div className="home-summary-card">
              <span className="home-summary-label">Salariés</span>
              <strong>
                {loading ? <span className="home-skeleton"></span> : <CountUp start={0} end={stats.cdiCount} duration={1} />}
              </strong>
              <p>Comptes CDI suivis dans le portail</p>
            </div>

            <div className="home-summary-card">
              <span className="home-summary-label">Déclarations</span>
              <strong>
                {loading ? <span className="home-skeleton"></span> : <CountUp start={0} end={stats.declarationCount} duration={1} />}
              </strong>
              <p>Déclarations enregistrées</p>
            </div>

            <div className="home-summary-card">
              <span className="home-summary-label">Fiches de paie</span>
              <strong>
                {loading ? <span className="home-skeleton"></span> : <CountUp start={0} end={stats.paieCount} duration={1} />}
              </strong>
              <p>Documents de paie disponibles</p>
            </div>
          </section>
        )}

        {isCDI && (
          <section className="home-summary-grid">
            <div className="home-summary-card">
              <span className="home-summary-label">Mes déclarations</span>
              <strong>
                {loading ? <span className="home-skeleton"></span> : <CountUp start={0} end={stats.myDeclarationCount} duration={1} />}
              </strong>
              <p>Déclarations liées à mon compte</p>
            </div>

            <div className="home-summary-card">
              <span className="home-summary-label">Mes documents</span>
              <strong>
                {loading ? <span className="home-skeleton"></span> : <CountUp start={0} end={stats.myDocCount} duration={1} />}
              </strong>
              <p>Documents administratifs disponibles</p>
            </div>

            <div className="home-summary-card">
              <span className="home-summary-label">Mes fiches de paie</span>
              <strong>
                {loading ? <span className="home-skeleton"></span> : <CountUp start={0} end={stats.myPaieCount} duration={1} />}
              </strong>
              <p>Fiches accessibles depuis le portail</p>
            </div>
          </section>
        )}

        <section className="home-actions-grid">
          <article className="home-action-card">
            <div className="home-action-icon">01</div>
            <h2>Saisir les jours de travail</h2>
            <p>Déclarez rapidement les jours travaillés, congés ou absences.</p>
            <button onClick={handleDeclarationClick}>
              Commencer
            </button>
          </article>

          <article className="home-action-card">
            <div className="home-action-icon">02</div>
            <h2>Consulter les fiches de paie</h2>
            <p>Retrouvez vos documents de paie dans un espace centralisé.</p>
            <button onClick={() => navigate('/Paie')}>
              Voir les fiches
            </button>
          </article>

          <article className="home-action-card">
            <div className="home-action-icon">03</div>
            <h2>Gestion des documents</h2>
            <p>Accédez aux documents importants et pièces administratives.</p>
            <button onClick={() => navigate('/Documents')}>
              Gérer les documents
            </button>
          </article>
        </section>

      </div>
    </LayoutDashboard>
  );
};

export default Home;